import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, X, CheckSquare, ArrowUp, ArrowDown } from "lucide-react";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { PageLoader } from "../shared/LoadingSpinner";
import { EmptyState } from "../shared/EmptyState";
import { toast } from "sonner";

const TICKET_TYPES = [
    { id: "incident", label: "Incident" }, { id: "problem", label: "Problème" },
    { id: "change", label: "Changement" }, { id: "service_request", label: "Demande de service" },
    { id: "access_request", label: "Demande d'accès" }, { id: "software_install", label: "Installation logiciel" },
    { id: "software_update", label: "Mise à jour logiciel" }, { id: "new_equipment", label: "Nouveau matériel" },
    { id: "onboarding", label: "Arrivée collaborateur" }, { id: "offboarding", label: "Départ collaborateur" },
    { id: "security", label: "Sécurité" }, { id: "question", label: "Question" },
    { id: "other", label: "Autre" }, { id: "password_reset", label: "Réinitialisation MDP" },
    { id: "defective_equipment", label: "Matériel défectueux" }, { id: "equipment_loan", label: "Prêt de matériel" },
];

const EMPTY_FORM = { name: "", ticket_types: [], items: [{ label: "" }], is_active: true };

export default function AdminChecklistTemplates() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    useEffect(() => { loadTemplates(); }, []);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const data = await api.getAllChecklistTemplates();
            setTemplates(data);
        } catch { toast.error("Erreur chargement templates"); }
        finally { setLoading(false); }
    };

    const openNew = () => { setEditing(null); setForm({ ...EMPTY_FORM, items: [{ label: "" }] }); setShowForm(true); };
    const openEdit = (t) => {
        setEditing(t.id);
        setForm({ name: t.name, ticket_types: t.ticket_types || [], items: t.items?.length > 0 ? t.items.map((i) => ({ label: i.label })) : [{ label: "" }], is_active: t.is_active });
        setShowForm(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
        const items = form.items.filter((i) => i.label.trim()).map((i) => ({ label: i.label.trim() }));
        if (items.length === 0) { toast.error("Au moins un item est requis"); return; }
        try {
            const payload = { ...form, items };
            if (editing) { await api.updateChecklistTemplate(editing, payload); toast.success("Template mis à jour"); }
            else { await api.createChecklistTemplate(payload); toast.success("Template créé"); }
            setShowForm(false);
            loadTemplates();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer ce template ?")) return;
        try { await api.deleteChecklistTemplate(id); toast.success("Template supprimé"); loadTemplates(); }
        catch { toast.error("Erreur"); }
    };

    const toggleType = (typeId) => {
        setForm((prev) => ({
            ...prev,
            ticket_types: prev.ticket_types.includes(typeId)
                ? prev.ticket_types.filter((t) => t !== typeId)
                : [...prev.ticket_types, typeId],
        }));
    };

    const moveItem = (idx, dir) => {
        const items = [...form.items];
        const swap = idx + dir;
        if (swap < 0 || swap >= items.length) return;
        [items[idx], items[swap]] = [items[swap], items[idx]];
        setForm({ ...form, items });
    };

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-checklist-templates">
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Templates de checklists</h1>
                    <p className="page-subtitle">{templates.length} template{templates.length !== 1 ? "s" : ""}</p>
                </div>
                <Button onClick={openNew} data-testid="new-template-btn"><Plus className="w-4 h-4 mr-2" />Nouveau template</Button>
            </div>

            {showForm && (
                <div className="card mb-6" data-testid="template-form">
                    <div className="card-header flex items-center justify-between">
                        <h2 className="text-sm font-semibold">{editing ? "Modifier le template" : "Nouveau template"}</h2>
                        <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="card-body space-y-4">
                        <div>
                            <label className="form-label">Nom *</label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Onboarding IT standard" data-testid="template-name-input" />
                        </div>
                        <div>
                            <label className="form-label">Types de tickets associés</label>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                                {TICKET_TYPES.map((tt) => (
                                    <button key={tt.id} type="button" onClick={() => toggleType(tt.id)}
                                        className={`px-2 py-0.5 text-xs rounded border transition-colors ${form.ticket_types.includes(tt.id) ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"}`}
                                        data-testid={`type-toggle-${tt.id}`}>{tt.label}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="form-label">Items</label>
                            <div className="space-y-1.5">
                                {form.items.map((item, idx) => (
                                    <div key={`item-${idx}-${item.label?.slice(0,10) || ''}`} className="flex items-center gap-1">
                                        <span className="text-xs text-gray-400 w-5 text-center">{idx + 1}</span>
                                        <Input value={item.label} onChange={(e) => {
                                            const items = [...form.items];
                                            items[idx] = { label: e.target.value };
                                            setForm({ ...form, items });
                                        }} placeholder="Libellé de l'item" className="h-8 text-sm flex-1" data-testid={`template-item-${idx}`} />
                                        <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-20"><ArrowUp className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => moveItem(idx, 1)} disabled={idx === form.items.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-20"><ArrowDown className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => { const items = form.items.filter((_, i) => i !== idx); setForm({ ...form, items: items.length ? items : [{ label: "" }] }); }} className="p-1 text-gray-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                ))}
                                <button type="button" onClick={() => setForm({ ...form, items: [...form.items, { label: "" }] })}
                                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 px-5 py-1" data-testid="add-template-item-btn">
                                    <Plus className="w-3 h-3" /> Ajouter un item
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="rounded border-gray-300" />
                                Active
                            </label>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
                            <Button onClick={handleSave} data-testid="save-template-btn">{editing ? "Enregistrer" : "Créer"}</Button>
                        </div>
                    </div>
                </div>
            )}

            {templates.length === 0 ? (
                <div className="card">
                    <EmptyState icon={CheckSquare} title="Aucun template" description="Créez des templates pour accélérer la création de checklists"
                        action={<Button onClick={openNew}><Plus className="w-4 h-4 mr-2" />Créer un template</Button>} />
                </div>
            ) : (
                <div className="space-y-3">
                    {templates.map((t) => (
                        <div key={t.id} className="card" data-testid={`template-${t.id}`}>
                            <div className="card-body">
                                <div className="flex items-start justify-between mb-2">
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-900">{t.name}</h3>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {(t.ticket_types || []).map((tt) => (
                                                <span key={tt} className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{TICKET_TYPES.find((x) => x.id === tt)?.label || tt}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {!t.is_active && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Inactive</span>}
                                        <button onClick={() => openEdit(t)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-template-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => handleDelete(t.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`delete-template-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                                <div className="text-xs text-gray-500 space-y-0.5">
                                    {(t.items || []).map((item, idx) => (
                                        <div key={`${t.id}-item-${idx}`} className="flex items-center gap-1.5">
                                            <span className="w-3.5 h-3.5 border border-gray-300 rounded flex-shrink-0" />
                                            <span>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
